import React, { useCallback } from 'react';
import _ from 'lodash';
import { WniInputText } from 'wni-common-base-components';
import PropTypes from 'prop-types';
import { useTranslator } from '@jutro/locale';
import { ViewModelForm } from '@xengage/gw-portals-viewmodel-react';
import { showProdsCompltdOpsActual, PREMISES_PRODUCTSOPS_SUBLINE } from '../../utils/GLPremiumReportUtil';
import metadata from './SublineDetailComponent.metadata.json5';
import messages from './SublineDetailComponent.messages';
import commonStyles from '../../CEGLPremiumReportWizard.module.scss';

function SublineDetailComponent(props) {
    const translator = useTranslator();
    const {
        data: sublineVM,
        wizardData,
        updateWizardData,
        sublinePath
    } = props;

    const { 
        jobStatus
    } = wizardData.value;

    const readOnly = jobStatus === 'AuditComplete' || jobStatus === 'Quoted';
    const sublineName = _.get(sublineVM, 'sublineName.value');
    const isPremisesProductsOpsSubline = sublineName === PREMISES_PRODUCTSOPS_SUBLINE;
    const hasProdsCompltdOpsActualColumn = showProdsCompltdOpsActual(_.get(sublineVM, 'exposures.value'));
    const renderCell = (item, index, property) => {
        return item[property.id].value;
    };

    const writeValue = useCallback((newValue, selectedBasePath, propertyId, item) => {
        const trimmedValue = _.isString(newValue) ? newValue.trim() : newValue;
        const isBlank = _.isNil(trimmedValue) || trimmedValue === '';
        const isNegative = Number(trimmedValue) < 0;
        const numericValue = _.toNumber(trimmedValue);
        const isNumber = !isBlank && !_.isNaN(numericValue);
        const value = isBlank ? undefined : trimmedValue;
        const selectedColumnPath = `${selectedBasePath}.${propertyId}`;
        _.set(wizardData, `${selectedColumnPath}.value`, value);
        _.set(wizardData, `${selectedBasePath}.value.${propertyId}`, value);
        const validationMsgPath = `${selectedColumnPath}.validationMsgs`;

        if (!isBlank && isNegative) {
            _.set(wizardData, validationMsgPath, translator(messages.valueMustBeZeroOrGreater));
        } else if (!isBlank && !isNumber) {
            _.set(wizardData, validationMsgPath, translator(messages.inputMustBeNumber));
        } else {
            _.set(wizardData, validationMsgPath, undefined);
        }

        const isPairedExposure = item.value.sublineTypeName === PREMISES_PRODUCTSOPS_SUBLINE
            && _.get(item, 'canEditProdsCompltdOpsActual.value')
            && _.includes(['premisesOperationsActual', 'prodsCompltdOpsActual'], propertyId);

        if (isPairedExposure) {
            const pairedPropertyId = propertyId === 'premisesOperationsActual'
                ? 'prodsCompltdOpsActual'
                : 'premisesOperationsActual';
            _.set(wizardData, `${selectedBasePath}.${pairedPropertyId}.value`, value);
            _.set(wizardData, `${selectedBasePath}.value.${pairedPropertyId}`, value);
        }

        updateWizardData(wizardData);
    }, [translator, updateWizardData, wizardData]);

    const renderActualCell = (item, index, property) => {
        const selectedBasePath = `${sublinePath}.exposures.children[${index}]`;
        const propertyId = property.id;
        const selectedActualPath = `${selectedBasePath}.${propertyId}`;

        const validationErrorMessage = _.get(item, `${propertyId}.validationMsgs`);
        const hasValidationError = !_.isEmpty(validationErrorMessage);

        const actualValue = _.get(
            item,
            `${propertyId}.value`,
            _.get(item, `value.${propertyId}`)
        );

        if (readOnly) {
            return actualValue ?? renderCell(item, index, property);
        }

        const canInputFields = ['premisesOperationsActual', 'prodsCompltdOpsActual', 'actual'];
        const canInput = _.includes(canInputFields, propertyId);

        const isActualCanEdit = propertyId === 'prodsCompltdOpsActual'
            ? _.get(item, 'canEditProdsCompltdOpsActual.value')
            : true;

        const isActualReadOnly = readOnly ? true : !isActualCanEdit;

        if (canInput) {
            return (
                <WniInputText
                    id={`${propertyId}_${index}_${_.get(item, 'publicID.value')}`}
                    path={selectedActualPath}
                    onValueChange={(newValue) => writeValue(newValue, selectedBasePath, propertyId, item)}
                    value={_.isNil(actualValue) ? '' : String(actualValue)}
                    readOnly={isActualReadOnly}
                    required
                    validationMessages={hasValidationError ? [validationErrorMessage] : undefined}
                    showErrors={hasValidationError}
                    hideLabel
                />
            );
        }

        return null;
    };

    const overrides = {
        sublineTitle: {
            content: sublineName
        },
        premisesProductsOpsExposuresContainer: {
            visible: isPremisesProductsOpsSubline
        },
        exposuresWithProdsCompltdOpsActualTable: {
            visible: hasProdsCompltdOpsActualColumn,
            data: sublineVM.exposures.children
        },
        exposuresWithoutProdsCompltdOpsActualTable: {
            visible: !hasProdsCompltdOpsActualColumn,
            data: sublineVM.exposures.children
        },
        otherSublineExposuresTable: {
            visible: !isPremisesProductsOpsSubline,
            data: sublineVM.exposures.children
        }
    };

    const resolvers = {
        resolveClassNameMap: commonStyles,
        resolveCallbackMap: {
            renderCell,
            renderActualCell
        }     
    };

    return (
        <ViewModelForm
            uiProps={metadata.componentContent}
            model={sublineVM}
            overrideProps={overrides}
            classNameMap={resolvers.resolveClassNameMap}
            callbackMap={resolvers.resolveCallbackMap}
        />
    );
}

SublineDetailComponent.propTypes = {
    data: PropTypes.object.isRequired,
    wizardData: PropTypes.object.isRequired,
    updateWizardData: PropTypes.func.isRequired,
    sublinePath: PropTypes.string.isRequired
};

export default SublineDetailComponent;
